import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Modal from '../../components/Modal';
import OutsideClickHandler from 'react-outside-click-handler';
import { isEqual } from 'lodash';
import moment from 'moment';

import { DateTime, Picker, Time, WithErrors } from 'types/appointments';
import Previewer from 'screens/Appointments/components/Previewer';
import { useModal } from 'screens/Appointments/hooks/useModal';
import styles from './TimePicker.module.less';
import { ChevronRightIcon } from 'components/Icons';
import { Calendar } from '../../components/Calendar';
import { formatHours, TimePicker } from '../TimePicker';

export const DateTimePicker: React.FC<Picker<DateTime> & WithErrors> = ({
  defaultValue,
  error,
  onClose,
  disabled,
  clearError,
}) => {
  const { t } = useTranslation();

  const { isOpen, open, close, confirm, picked, onPick } = useModal(onClose, defaultValue);

  const displayValue = [
    defaultValue.date && moment(defaultValue.date).format('D MMMM'),
    [defaultValue.time?.start, defaultValue.time?.end]
      .filter(Boolean)
      .map(formatHours)
      .join(' - '),
  ]
    .filter(Boolean)
    .join(' ');

  const [isCalendarOpened, setIsCalendarOpened] = useState(false);

  const handleDateChange = (date: Date) => {
    onPick({
      time: picked?.time ?? {},
      date,
    });
    setIsCalendarOpened(false);
  };

  const handleTimeChange = (time: Time) => {
    onPick({
      time,
      date: picked?.date,
    });
  };

  const onSubmit = () => {
    clearError('dateSlot');
    confirm();
  };

  return (
    <>
      <Previewer
        displayValue={displayValue}
        onClick={open}
        title={displayValue || error ? t('appointments.datetime_title') : t('appointments.selector_datetime')}
        disabled={disabled}
        errorMessage={error ? t('appointments.selector_datetime') : ''}
      />
      {isOpen && (
        <Modal>
          <OutsideClickHandler
            onOutsideClick={() => {
              onPick(defaultValue);
              setIsCalendarOpened(false);
              close();
            }}
          >
            <Modal.Header
              title={t('appointments.datetime_title')}
              renderArrow={isCalendarOpened}
              onArrowClick={() => setIsCalendarOpened(false)}
              onClose={() => {
                onPick(defaultValue);
                setIsCalendarOpened(false);
                close();
              }}
            />
            <Modal.Body className={styles.modalBody}>
              {isCalendarOpened ? (
                <Calendar date={picked?.date ?? new Date()} onChange={handleDateChange} />
              ) : (
                <>
                  <section
                    onClick={() => setIsCalendarOpened(true)}
                    className={classNames(styles.section, styles.datePicker)}
                    role="presentation"
                  >
                    <span>{t('appointments.date')}</span>
                    <div>
                      <span className={classNames(styles.value, picked?.date && styles.filled)}>
                        {picked?.date ? moment(picked.date).format('DD MMMM') : t('appointments.selector_date')}
                      </span>
                      <ChevronRightIcon className={styles.chevronRight} />
                    </div>
                  </section>
                  <section className={styles.section}>
                    <span>{t('appointments.time')}</span>
                    <span className={classNames(styles.value, picked?.time?.start && styles.filled)}>
                      {picked?.time?.start
                        ? [picked.time?.start, picked.time?.end].map(formatHours).join(' - ')
                        : t('appointments.selector_time')}
                    </span>
                  </section>
                  <TimePicker time={picked?.time ?? {}} onChange={handleTimeChange} />
                </>
              )}
            </Modal.Body>
            {!isCalendarOpened && <Modal.Confirm disabled={isEqual(picked, defaultValue)} onClick={onSubmit} />}
          </OutsideClickHandler>
        </Modal>
      )}
    </>
  );
};
